import { Globe } from "lucide-react";
import MultiSelectField from "./form/MultiSelectField";
import InputField from "./form/InputField";
import { LANGUAGE_OPTIONS, OTHER_LANGUAGE_VALUE, getDialectOptions } from "../../shared/constants/languages";
import { useLanguage } from "../../context/LanguageContext";

interface Props {
  languages: string[];
  dialects: string[];
  onLanguagesChange: (langs: string[]) => void;
  onDialectsChange: (dialects: string[]) => void;
  customLanguage?: string;
  onCustomLanguageChange?: (value: string) => void;
  error?: string;
}

export default function LanguageDialectSelect({
  languages, dialects, onLanguagesChange, onDialectsChange,
  customLanguage = "", onCustomLanguageChange, error,
}: Props) {
  const { t } = useLanguage();
  const handleLanguagesChange = (newLangs: string[]) => {
    const validDialectCodes = new Set(
      newLangs.flatMap((code) => getDialectOptions(code).map((d) => d.value))
    );
    onDialectsChange(dialects.filter((d) => validDialectCodes.has(d)));
    onLanguagesChange(newLangs);
  };

  return (
    <div className="space-y-4">
      <MultiSelectField
        label={t("shared.languageDialectSelect.languages")}
        icon={Globe}
        options={LANGUAGE_OPTIONS}
        selected={languages}
        onChange={handleLanguagesChange}
        error={error}
      />

      {languages.includes(OTHER_LANGUAGE_VALUE) && (
        <div className="ml-4 pl-4 border-l-2 border-[#b3d130]/40">
          <InputField
            label={t("shared.languageDialectSelect.otherLanguageLabel")}
            icon={Globe}
            value={customLanguage}
            onChange={(e) => onCustomLanguageChange?.(e.target.value)}
            placeholder={t("shared.languageDialectSelect.otherLanguagePlaceholder")}
          />
        </div>
      )}

      {languages.map((langCode) => {
        const dialectOpts = getDialectOptions(langCode);
        if (!dialectOpts.length) return null;
        const langLabel = LANGUAGE_OPTIONS.find((l) => l.value === langCode)?.label ?? langCode;
        return (
          <div key={langCode} className="ml-4 pl-4 border-l-2 border-[#b3d130]/40">
            <MultiSelectField
              label={`${langLabel} — ${t("shared.languageDialectSelect.dialectAccent")}`}
              icon={Globe}
              options={dialectOpts}
              selected={dialects.filter((d) => dialectOpts.some((o) => o.value === d))}
              onChange={(selected) => {
                const otherDialects = dialects.filter((d) => !dialectOpts.some((o) => o.value === d));
                onDialectsChange([...otherDialects, ...selected]);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
